import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { FaFileUpload, FaTimes, FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import { auth } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  fetchEmployeeCvDocuments,
  getMissingRequiredDocuments,
} from '../../utils/cvDocuments';
import styles from './MissingDocumentsPrompt.module.css';

// Hatırlatma HER GİRİŞTE bir kez çıkar.
//
// Anahtar yalnızca sekmeye değil, oturumun kendisine bağlıdır: Firebase'in
// `lastSignInTime` damgası her yeni girişte değiştiği için çıkış yapıp tekrar
// giren kullanıcı uyarıyı yeniden görür. Aynı oturumda sayfa gezerken damga
// sabit kaldığından uyarı tekrar açılmaz ve Firestore'a tekrar sorulmaz.
const CHECK_KEY_PREFIX = 'missingCvDocumentsChecked';

const sessionKeyFor = (user) =>
  `${CHECK_KEY_PREFIX}:${user?.uid || 'anon'}:${
    user?.metadata?.lastSignInTime || 'session'
  }`;

// Çıkışta işaretleri sil: lastSignInTime beklenmedik şekilde aynı kalsa bile
// bir sonraki girişte kontrol yeniden yapılır.
const clearCheckMarks = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(CHECK_KEY_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch (e) {
    // sessionStorage kapalıysa yapacak bir şey yok
  }
};

/**
 * Zorunlu CV/GCP/IATA belgelerini yüklememiş çalışana girişte bir kez
 * gösterilen hatırlatma. Kullanıcıyı doğrudan ilgili profil sekmesine yollar.
 */
const MissingDocumentsPrompt = () => {
  const navigate = useNavigate();
  const { employeeId, isLoading } = useUserProfile();
  const [missing, setMissing] = useState([]);
  const [show, setShow] = useState(false);
  // Giriş/çıkış geçişlerini yakalamak için auth kullanıcısı izlenir; böylece
  // yeni girişte efekt yeniden çalışır ve yeni oturum anahtarı üretilir.
  const [authUser, setAuthUser] = useState(() => auth.currentUser);

  useEffect(() => onAuthStateChanged(auth, setAuthUser), []);

  useEffect(() => {
    if (!authUser) {
      // Çıkış yapıldı: açık hatırlatma kapansın, işaretler sıfırlansın
      setShow(false);
      setMissing([]);
      clearCheckMarks();
      return;
    }
    if (isLoading || !employeeId) return;

    const sessionKey = sessionKeyFor(authUser);
    let alreadyChecked = false;
    try {
      alreadyChecked = Boolean(sessionStorage.getItem(sessionKey));
    } catch (e) {
      // sessionStorage kapalıysa kontrol her açılışta yapılır
    }
    if (alreadyChecked) return;

    let cancelled = false;
    const checkDocuments = async () => {
      try {
        const documents = await fetchEmployeeCvDocuments(employeeId);
        if (cancelled) return;

        const missingTypes = getMissingRequiredDocuments(documents);
        try {
          sessionStorage.setItem(sessionKey, '1');
        } catch (e) {
          // yoksay
        }
        if (missingTypes.length > 0) {
          setMissing(missingTypes);
          setShow(true);
        }
      } catch (error) {
        // Belgeler okunamazsa kullanıcıyı yanlış uyarmaktansa sessiz kal
        console.error('CV/GCP/IATA belge kontrolü başarısız:', error);
      }
    };

    checkDocuments();
    return () => {
      cancelled = true;
    };
  }, [authUser, employeeId, isLoading]);

  if (!show || missing.length === 0) return null;

  const goToDocuments = () => {
    setShow(false);
    // İlk eksik belgenin sekmesi açık gelsin
    navigate('/profile', { state: { activeTab: missing[0].profileTab } });
  };

  return (
    <div className={styles.overlay} onClick={() => setShow(false)}>
      <div className={styles.prompt} onClick={(e) => e.stopPropagation()} role="alertdialog">
        <div className={styles.header}>
          <h2 className={styles.title}>
            <FaExclamationTriangle className={styles.titleIcon} />
            Eksik belgeleriniz var
          </h2>
          <button
            type="button"
            className={styles.close}
            onClick={() => setShow(false)}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.body}>
          <p className={styles.lead}>
            Aşağıdaki belgeleri henüz yüklemediniz. Profilinizdeki{' '}
            <strong>CV/GCP/IATA</strong> bölümünden yükleyebilirsiniz.
          </p>
          <ul className={styles.list}>
            {missing.map((type) => (
              <li key={type.key} className={styles.item}>
                <FaFileUpload className={styles.itemIcon} />
                <span className={styles.itemLabel}>{type.label}</span>
                <span className={styles.itemBadge}>Yüklenmedi</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={() => setShow(false)}>
            Daha sonra
          </button>
          <button type="button" className={styles.primary} onClick={goToDocuments}>
            Belgelerimi yükle <FaArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingDocumentsPrompt;
